import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Building2, CheckCircle2 } from "lucide-react";
import { api, stageClass, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NewApplicationDialog from "../components/NewApplicationDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const ACCEPTED = ["Fees Paid", "PAL Pending", "PAL Issued", "Admission Complete"];

export default function B2BStudentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/b2b/students/${id}`), api.get("/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const s = data.student;
  const save = async (body) => {
    try { await api.patch(`/b2b/students/${id}`, body); toast.success("Student updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const blurSave = (k) => (e) => e.target.value !== (s[k] || "") && save({ [k]: e.target.value });

  return (
    <div className="space-y-6">
      <Link to="/students" data-testid="back-students" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Back to students
      </Link>
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center heading-font text-xl font-bold">
            {s.student_name?.split(" ").map(x => x[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="student-name" className="heading-font text-2xl font-bold text-slate-900">{s.student_name}</h1>
              <span className="mono text-xs text-slate-500">{s.student_code}</span>
            </div>
            {s.also_known_as?.length > 0 && <div data-testid="student-aka" className="text-xs text-slate-500 mt-1">Also known as: {s.also_known_as.join(", ")}</div>}
            {data.partner && <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-600"><Building2 className="w-3 h-3" />{data.partner.company_name}</div>}
          </div>
        </div>
        <Button data-testid="student-add-offer-btn" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Application
        </Button>
      </div>

      <Tabs defaultValue="applications">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="student-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="applications" data-testid="student-tab-applications">Applications ({data.applications.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="student-tab-documents">Documents ({data.documents.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="student-tab-notes">Notes &amp; History</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="student-tab-tasks">Tasks ({data.tasks.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
      <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-3 gap-4" key={JSON.stringify(s)}>
        <F label="Full Name"><Input data-testid="student-edit-name" defaultValue={s.student_name} onBlur={blurSave("student_name")} /></F>
        <F label="Email"><Input data-testid="student-edit-email" defaultValue={s.email} onBlur={blurSave("email")} /></F>
        <F label="Phone"><Input data-testid="student-edit-phone" defaultValue={s.phone} onBlur={blurSave("phone")} /></F>
        <F label="Date of Birth"><Input data-testid="student-edit-dob" type="date" defaultValue={s.date_of_birth || ""} onBlur={blurSave("date_of_birth")} /></F>
        <F label="Location">
          <Select value={s.student_location || ""} onValueChange={v => save({ student_location: v })}>
            <SelectTrigger data-testid="student-edit-location"><SelectValue placeholder="Onshore / Offshore" /></SelectTrigger>
            <SelectContent>{["Onshore", "Offshore"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>
        </TabsContent>
        <TabsContent value="applications"><OffersList apps={data.applications} onOpen={appId => nav(`/applications/${appId}`)} /></TabsContent>
        <TabsContent value="documents"><TagList testid="student-docs" rows={data.documents} empty="No documents uploaded yet." render={d => <>
          <a className="text-orange-700 hover:underline truncate min-w-0" title={d.original_filename} onClick={e => { e.preventDefault(); openFile(d.id); }} href="#">{d.original_filename}</a>
          <span className="text-xs text-slate-500 shrink-0">{d.doc_type}</span></>} /></TabsContent>
        <TabsContent value="notes"><TagList testid="student-notes" rows={[...data.notes.map(n => ({ ...n, _t: "Note" })), ...data.history.map(h => ({ ...h, _t: "History" }))].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))}
          empty="No notes or history yet." render={r => <>
          <span className={`text-[10px] uppercase rounded px-1.5 py-0.5 shrink-0 ${r._t === "Note" ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-600"}`}>{r._t}</span>
          <span className="min-w-0 truncate" title={r.text || r.details}>{r.text || `${(r.action || "").replace(/_/g, " ")} — ${r.details || ""}`}</span>
          <span className="text-xs text-slate-500 shrink-0 ml-auto">{r.author_name || r.actor_name || ""} · {fmtDate(r.created_at)}</span></>} /></TabsContent>
        <TabsContent value="tasks"><TagList testid="student-tasks" rows={data.tasks} empty="No tasks." render={t => <>
          <span className="min-w-0 truncate font-medium">{t.title}</span>
          <span className="text-xs text-slate-500 shrink-0 ml-auto">{t.status} · due {fmtDate(t.due_date)}</span></>} /></TabsContent>
      </Tabs>
      {meta && <NewApplicationDialog open={open} onOpenChange={setOpen} meta={meta} student={{ ...s, partner_name: data.partner?.company_name }}
        onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function OffersList({ apps, onOpen }) {
  const isAcc = a => ACCEPTED.includes(a.stage) || (a.deposit_paid && !["Backed Out", "Withdrawn", "Selected Another College"].includes(a.stage));
  const accepted = apps.find(isAcc);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="heading-font text-lg font-semibold text-slate-900">Applications ({apps.length})</h2>
        {accepted && <span data-testid="student-accepted-offer" className="inline-flex items-center gap-1 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Accepted: {accepted.college_code || accepted.college} · {accepted.application_id}</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {apps.map(a => (
          <button key={a.id} data-testid={`student-offer-${a.id}`} onClick={() => onOpen(a.id)}
            className={`text-left rounded-xl border p-4 bg-white transition-colors hover:border-orange-300 ${["Backed Out", "Withdrawn", "Selected Another College"].includes(a.stage) ? "opacity-60" : ""} ${isAcc(a) ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="mono text-xs text-slate-500">{a.application_id}</span>
              <span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span>
            </div>
            <div className="mt-2 font-semibold text-slate-900">{a.college_code ? `${a.college_code} — ${a.college}` : a.college}</div>
            <div className="text-sm text-slate-600">{a.course_name || a.program || "No course name"}</div>
            <div className="text-[11px] text-slate-500">{a.program_category || "Category needs review"}</div>
            <div className="mt-2 text-xs text-slate-500">{a.intake || "No intake"} · received {fmtDate(a.date_received)}</div>
            {(a.closure_reason || a.backed_out_reason) && <div className="mt-2 text-xs text-zinc-600 italic">{a.closure_reason || a.backed_out_reason}</div>}
          </button>
        ))}
        {apps.length === 0 && <div className="text-sm text-slate-500">No offer applications yet.</div>}
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;

const TagList = ({ rows, render, empty, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
    {rows.map((r, i) => (
      <div key={r.id || i} className="flex items-center gap-3 px-4 py-2 text-sm">
        {r.app_ref && <span className="mono text-[11px] rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5 shrink-0">{r.app_ref}</span>}
        {render(r)}
      </div>
    ))}
    {rows.length === 0 && <div className="px-4 py-6 text-sm text-slate-500">{empty}</div>}
  </div>
);

const openFile = async (fid) => {
  const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
};
